import { type AudioMaster, audioMaster, type MasterAudioContext } from '../audio/master';

/**
 * **Os dois sons da detonação, SINTETIZADOS** (gate V28G.N).
 *
 * ## Porque sintetizados, e não gravados
 *
 * Porque este jogo nunca tocou um som. `assets-source/audio/` está vazia, não há
 * um ficheiro de áudio no repositório, e não há uma linha que use `this.sound`,
 * `AudioContext` ou qualquer biblioteca de som. O dono pediu som para as garras
 * e para o corte, e havia duas maneiras honestas de responder: esperar por
 * ficheiros que não existem, ou fazer o som a partir de nada.
 *
 * Inventar um ficheiro de asset seria a terceira, e é a que não se faz:
 * `assets-source/` guarda MESTRES, e um mestre gerado por mim é um mestre sem
 * autor, sem licença e sem quem o aprove.
 *
 * Síntese não tem esse problema. Não descarrega nada, não pesa no bundle, não
 * tem licença, e o resultado é determinístico: a mesma garra soa igual em todas
 * as máquinas, sem um ficheiro para alguém trocar por engano.
 *
 * ## O que NÃO está aqui, e é preciso saber
 *
 * Não há controlo de volume nem botão de silêncio em lado nenhum do jogo,
 * porque nunca houve som para calar. O ganho é o {@link BLEED_AUDIO_GAIN}
 * abaixo, deliberadamente discreto, e é o único travão que existe hoje.
 *
 * ## Um contexto, e é o do Phaser
 *
 * O Phaser já cria um `AudioContext` e já o destranca no primeiro gesto do
 * jogador - que num duelo aconteceu muito antes de haver marcas para rebentar.
 * Criar um segundo contexto seria um segundo relógio de áudio e um segundo
 * recurso a fechar, pela mesma razão que há um só `Phaser.Game`.
 */

/**
 * O ganho de pico de um som desta joia.
 *
 * ## O que ele é, e o que NÃO é
 *
 * É o peso RELATIVO deste efeito dentro do jogo - quanto uma garra vale ao pé de
 * tudo o resto que um dia venha a soar. Não é o volume: o volume é do jogador, e
 * vive no ganho mestre (`audio/master.ts`).
 *
 * Os dois multiplicam-se. A 100 % o produto é exatamente este número, porque
 * multiplicar por um é a identidade - e é por isso que a fundação de volume
 * entrou sem alterar uma vírgula do som aprovado.
 */
export const BLEED_AUDIO_GAIN = 0.16;

/**
 * Quanto tempo dura o som de UMA garra.
 *
 * Cento e setenta, e não os cento e dez de uma primeira versão: atrito precisa
 * de DURAR para se ouvir como atrito. A cento e dez, o som acabava antes de o
 * ouvido lhe distinguir a textura, e o que ficava era o ataque - ou seja, uma
 * pancada.
 *
 * Mais longo do que o intervalo de 100 ms entre garras, e é deliberado: elas
 * sobrepõem-se, e a sobreposição é o que faz uma série soar a garras a rasgar
 * em vez de um metrónomo.
 */
export const BLEED_CLAW_SOUND_MS = 170;

/** E o do corte final, que é o golpe. */
export const BLEED_CUT_SOUND_MS = 380;

/**
 * **O que a apresentação pede ao som**, e é tudo o que ela sabe dele.
 *
 * Duas chamadas e nenhum conceito de áudio: o sistema das garras não conhece um
 * contexto, um ganho nem uma frequência, e é isso que o deixa ser provado com um
 * duplo que apenas conta.
 */
export interface BleedAudio {
  /** Uma garra entrou. `index` é a ordem dela na sequência, a partir de zero. */
  claw(index: number): void;
  /** O corte final abriu, com esta contagem de marcas por trás. */
  cut(marks: number): void;
}

/** O mínimo do `AudioContext` que este módulo usa. */
export interface BleedAudioContext {
  readonly currentTime: number;
  readonly sampleRate: number;
  readonly destination: AudioNode;
  readonly state: string;
  createBuffer(channels: number, length: number, sampleRate: number): AudioBuffer;
  createBufferSource(): AudioBufferSourceNode;
  createGain(): GainNode;
  createBiquadFilter(): BiquadFilterNode;
  createOscillator(): OscillatorNode;
}

/**
 * **Ruído liso**, para o que precisa de ser ar e não superfície.
 *
 * Um sopro não tem grão: é ruído contínuo, e a forma vem do filtro e do
 * envelope. É o que serve o corte.
 */
function noiseBuffer(context: BleedAudioContext, ms: number): AudioBuffer {
  const frames = Math.max(1, Math.round((context.sampleRate * ms) / 1_000));
  const buffer = context.createBuffer(1, frames, context.sampleRate);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < frames; i += 1) {
    data[i] = Math.random() * 2 - 1;
  }
  return buffer;
}

/**
 * **Ruído GRANULADO**, que é do que um arranhão é feito.
 *
 * ## Porque o ruído liso soava a batida
 *
 * Porque a diferença entre um impacto e um arranhão não está na altura nem na
 * banda: está na TEXTURA e no envelope. Ruído liso com queda imediata é o que
 * uma pancada faz - o dono ouviu-o e disse exatamente isso.
 *
 * Um arranhão é atrito: a garra salta ao longo da superfície, e cada salto é uma
 * variação rápida e IRREGULAR da amplitude. É isso que este buffer acrescenta -
 * uma modulação de algumas centenas de hertz, com o passo a variar, sobre o
 * ruído. O ouvido lê essa irregularidade como uma superfície a ser raspada.
 *
 * Regular seria pior do que nada: uma modulação certinha lê-se como um zumbido.
 */
function scratchBuffer(context: BleedAudioContext, ms: number): AudioBuffer {
  const frames = Math.max(1, Math.round((context.sampleRate * ms) / 1_000));
  const buffer = context.createBuffer(1, frames, context.sampleRate);
  const data = buffer.getChannelData(0);
  /* Os saltos, em amostras. Irregulares por construção, e não por sorte. */
  let until = 0;
  let level = 1;
  for (let i = 0; i < frames; i += 1) {
    if (i >= until) {
      /*
       * Um novo grão de 0,7 a 3,5 ms: a banda de 280 a 1400 saltos por segundo,
       * que é onde o atrito vive. Mais lento lê-se como estalidos separados;
       * mais rápido volta a ser ruído liso.
       */
      until = i + Math.round(context.sampleRate * (0.0007 + Math.random() * 0.0028));
      level = 0.35 + Math.random() * 0.65;
    }
    data[i] = (Math.random() * 2 - 1) * level;
  }
  return buffer;
}

/**
 * **O som real, sobre o contexto do Phaser.**
 *
 * Cada disparo cria os seus nós e deixa-os morrer com o `ended` do source: os
 * nós do Web Audio são descartáveis por desenho, e reutilizá-los seria guardar
 * estado que não paga nada.
 */
export class WebAudioBleedAudio implements BleedAudio {
  constructor(
    private readonly context: BleedAudioContext,
    /**
     * Para onde os sons saem: o ganho MESTRE, e não o destino do contexto.
     *
     * Ligar ao destino era ligar às colunas por cima de qualquer volume. É esta
     * linha - e só esta - que faz o controlo do jogador chegar a este efeito.
     */
    private readonly output: AudioNode,
  ) {}

  claw(index: number): void {
    if (this.context.state !== 'running') {
      /*
       * Um contexto suspenso é um contexto que o browser ainda não destrancou.
       * Não se força e não se rebenta: perde-se o som e o efeito continua.
       */
      return;
    }
    const now = this.context.currentTime;
    const life = BLEED_CLAW_SOUND_MS / 1_000;
    const source = this.context.createBufferSource();
    source.buffer = scratchBuffer(this.context, BLEED_CLAW_SOUND_MS);

    const band = this.context.createBiquadFilter();
    band.type = 'bandpass';
    /*
     * A ressonância DESLOCA-SE enquanto a garra arrasta.
     *
     * Uma frequência fixa é um objeto parado a soar; uma que cai é um ponto de
     * contacto a mudar - que é o que acontece quando algo é riscado. Foi a
     * segunda metade do que fazia isto soar a batida.
     *
     * O ponto de partida SOBE ao longo da sequência: dez garras iguais lêem-se
     * como repetição, a subir lêem-se como uma série a apertar. O tecto impede
     * que a décima quinta seja um assobio.
     */
    const start = Math.min(5_200, 2_600 + index * 150);
    band.frequency.setValueAtTime(start, now);
    band.frequency.exponentialRampToValueAtTime(start * 0.32, now + life);
    /*
     * `Q` alto: uma banda estreita é o que dá o timbre metálico do risco. A 1,4
     * o som era largo demais para se distinguir de um baque.
     */
    band.Q.value = 7;

    const gain = this.context.createGain();
    /*
     * **Ataque, corpo, queda** - e não um pico seguido de silêncio.
     *
     * Doze milissegundos a subir são o que separa um arranhão de uma pancada:
     * uma pancada começa no máximo, um arranhão ganha pressão. Depois sustenta,
     * porque atrito dura, e só então cai.
     */
    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.exponentialRampToValueAtTime(BLEED_AUDIO_GAIN, now + 0.012);
    gain.gain.exponentialRampToValueAtTime(BLEED_AUDIO_GAIN * 0.55, now + life * 0.55);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + life);

    source.connect(band);
    band.connect(gain);
    gain.connect(this.output);
    source.start(now);
    source.stop(now + life);
  }

  cut(marks: number): void {
    if (this.context.state !== 'running') {
      return;
    }
    const now = this.context.currentTime;
    const life = BLEED_CUT_SOUND_MS / 1_000;
    /* Mais marcas, mais corpo - com tecto, como as garras têm. */
    const weight = Math.min(1, marks / 12);

    /*
     * **Camada um: o SOPRO.**
     *
     * O que faz um golpe ler-se como um golpe é o ar deslocado por uma lâmina a
     * passar - ruído de banda larga cuja frequência SOBE depressa e depois cai,
     * porque a lâmina aproxima-se e afasta-se num instante.
     *
     * A versão anterior fazia o contrário: um serrote a descer de 300 Hz para
     * 48. Uma descida grave lê-se como uma explosão ou um zap, e foi por isso
     * que o dono disse que não parecia um corte.
     */
    const air = this.context.createBufferSource();
    air.buffer = noiseBuffer(this.context, BLEED_CUT_SOUND_MS);
    const sweep = this.context.createBiquadFilter();
    sweep.type = 'bandpass';
    sweep.Q.value = 1.1;
    sweep.frequency.setValueAtTime(420, now);
    /* Sobe em 70 ms - a passagem - e desce ao longo do resto: o afastamento. */
    sweep.frequency.exponentialRampToValueAtTime(3_600, now + 0.07);
    sweep.frequency.exponentialRampToValueAtTime(700, now + life);
    const airGain = this.context.createGain();
    airGain.gain.setValueAtTime(0.0001, now);
    airGain.gain.exponentialRampToValueAtTime(BLEED_AUDIO_GAIN * (0.6 + 0.4 * weight), now + 0.02);
    airGain.gain.exponentialRampToValueAtTime(0.0001, now + life);
    air.connect(sweep);
    sweep.connect(airGain);
    airGain.connect(this.output);
    air.start(now);
    air.stop(now + life);

    /*
     * **Camada dois: o GUME.**
     *
     * Uma ressonância aguda e curta, logo a seguir ao sopro. É o que dá a
     * qualidade metálica - o brilho de uma lâmina - e é o que distingue um corte
     * de um sopro qualquer.
     *
     * Uma onda simples e não ruído: aqui quer-se uma altura definida, que é
     * exatamente o contrário do que a garra quer.
     */
    const edge = this.context.createOscillator();
    edge.type = 'triangle';
    edge.frequency.setValueAtTime(3_100 + 400 * weight, now + 0.03);
    edge.frequency.exponentialRampToValueAtTime(1_500, now + 0.2);
    const edgeGain = this.context.createGain();
    edgeGain.gain.setValueAtTime(0.0001, now);
    edgeGain.gain.exponentialRampToValueAtTime(BLEED_AUDIO_GAIN * 0.35, now + 0.035);
    edgeGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.22);
    edge.connect(edgeGain);
    edgeGain.connect(this.output);
    edge.start(now);
    edge.stop(now + 0.24);

    /*
     * **Camada três: o PESO.**
     *
     * Um golpe que só tem sopro e gume é fino. Um baixo curto e filtrado por
     * baixo dá-lhe massa - é o impacto do corte a assentar, e é a camada que
     * cresce com a contagem.
     *
     * Curto de propósito: prolongá-lo transformaria o corte numa explosão, que é
     * o que o §7 proíbe.
     */
    const body = this.context.createBufferSource();
    body.buffer = noiseBuffer(this.context, 120);
    const low = this.context.createBiquadFilter();
    low.type = 'lowpass';
    low.frequency.setValueAtTime(900, now);
    low.frequency.exponentialRampToValueAtTime(180, now + 0.12);
    const bodyGain = this.context.createGain();
    bodyGain.gain.setValueAtTime(BLEED_AUDIO_GAIN * (0.3 + 0.5 * weight), now);
    bodyGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.12);
    body.connect(low);
    low.connect(bodyGain);
    bodyGain.connect(this.output);
    body.start(now);
    body.stop(now + 0.12);
  }
}

/**
 * O som desta cena, ou `null` se não houver contexto de áudio.
 *
 * `null` e não um objeto mudo: quem chama já trata a ausência - o efeito desenha
 * na mesma - e um duplo silencioso escondia a diferença entre "não há áudio" e
 * "o áudio não fez nada".
 */
export function bleedAudioFrom(
  manager: { readonly context?: unknown } | undefined,
  master: AudioMaster = audioMaster,
): BleedAudio | null {
  const context = manager?.context as BleedAudioContext | undefined;
  if (!context || typeof context.createBufferSource !== 'function') {
    return null;
  }
  /*
   * O contexto do Phaser é ADOTADO, e nunca criado.
   *
   * Idempotente: a caça e o duelo constroem sistemas de som novos sempre que
   * abrem, e todos chamam isto. `attach` responde ao segundo com o mesmo nó, o
   * que é o que impede uma sessão longa de acumular ganhos ligados ao destino.
   */
  master.attach(context as unknown as MasterAudioContext);
  const output = master.output();
  if (!output) {
    return null;
  }
  return new WebAudioBleedAudio(context, output);
}
